import { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { reconhecerAlerta } from "../context/apiAcoes";
import useStyles from "./styles";

// Modal do marco "Reconhecimento" da linha do tempo do incidente: reconhece
// o evento no Zabbix e, opcionalmente, cria a regra de reconhecimento
// automático — novos disparos da MESMA trigger neste MESMO equipamento são
// reconhecidos automaticamente enquanto o período não expira.

const UNIDADES = [
	{ valor: "minutos", rotulo: "minutos", fatorMinutos: 1 },
	{ valor: "horas", rotulo: "horas", fatorMinutos: 60 },
	{ valor: "dias", rotulo: "dias", fatorMinutos: 24 * 60 },
];

// Backend recusa acima de 7 dias (PERIODO_MAX_MINUTOS em reconhecerAlerta.js).
const PERIODO_MAX_MINUTOS = 7 * 24 * 60;

const QUANTIDADE_PADRAO = 2;
const UNIDADE_PADRAO = "horas";

export default function ModalReconhecerAlerta({
	aberto,
	incidente,
	onFechar,
	onReconhecido,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);

	const [mensagem, setMensagem] = useState("");
	const [reconhecerProximos, setReconhecerProximos] = useState(false);
	const [quantidade, setQuantidade] = useState(QUANTIDADE_PADRAO);
	const [unidade, setUnidade] = useState(UNIDADE_PADRAO);
	const [enviando, setEnviando] = useState(false);
	const [erro, setErro] = useState(null);

	const alerta = incidente?.alerta;
	if (!alerta) return null;

	// Cluster: reconhecer o incidente reconhece TODOS os alertas agrupados
	// (primário primeiro — o backend usa o primeiro id como referência).
	const idsDoIncidente = [
		alerta.id,
		...(alerta.alertasAgrupados || [])
			.map((item) => item.id)
			.filter((id) => id !== alerta.id),
	];
	const totalDoIncidente = idsDoIncidente.length;

	const fatorMinutos =
		UNIDADES.find((u) => u.valor === unidade)?.fatorMinutos || 1;
	const periodoMinutos = Math.round(Number(quantidade) * fatorMinutos);
	const periodoInvalido =
		reconhecerProximos &&
		(!Number.isFinite(periodoMinutos) ||
			periodoMinutos < 1 ||
			periodoMinutos > PERIODO_MAX_MINUTOS);

	const fechar = () => {
		if (enviando) return;
		setErro(null);
		onFechar();
	};

	const confirmar = async () => {
		setEnviando(true);
		setErro(null);
		try {
			await reconhecerAlerta({
				eventIds: idsDoIncidente,
				hostId: alerta.hostId,
				mensagem: mensagem.trim() || undefined,
				reconhecerProximos,
				periodoMinutos: reconhecerProximos ? periodoMinutos : undefined,
			});
			onReconhecido();
		} catch (e) {
			setErro(
				e?.response?.data?.message ||
					"Não foi possível reconhecer o alerta. Tente novamente.",
			);
		} finally {
			setEnviando(false);
		}
	};

	return (
		<Dialog open={Boolean(aberto)} onClose={fechar} maxWidth="sm" fullWidth>
			<DialogTitle sx={styles.titulo}>Reconhecer alerta</DialogTitle>
			<DialogContent sx={styles.conteudo}>
				<Box sx={styles.resumoAlerta}>
					<Typography sx={styles.tituloAlerta}>
						{alerta.titulo || "Alerta sem título"}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{[
							incidente.equipamento?.nome || alerta.hostId,
							dayjs(alerta.dataHoraQueda).format("DD/MM/YYYY HH:mm"),
							totalDoIncidente > 1
								? `${totalDoIncidente} alertas agrupados — todos serão reconhecidos`
								: null,
						]
							.filter(Boolean)
							.join(" · ")}
					</Typography>
				</Box>

				<TextField
					label="Mensagem (opcional)"
					size="small"
					multiline
					minRows={2}
					value={mensagem}
					onChange={(evento) => setMensagem(evento.target.value)}
					inputProps={{
						maxLength: 1500,
						"aria-label": "Mensagem do reconhecimento",
					}}
				/>

				<FormControlLabel
					control={
						<Checkbox
							checked={reconhecerProximos}
							onChange={(evento) => setReconhecerProximos(evento.target.checked)}
							size="small"
						/>
					}
					label="Reconhecer próximos alertas do mesmo tipo"
				/>

				{reconhecerProximos && (
					<Box sx={styles.blocoPeriodo}>
						<Box sx={styles.linhaPeriodo}>
							<TextField
								label="Período"
								type="number"
								size="small"
								value={quantidade}
								onChange={(evento) => setQuantidade(evento.target.value)}
								inputProps={{ min: 1, "aria-label": "Duração do período" }}
								sx={styles.campoQuantidade}
								error={periodoInvalido}
							/>
							<TextField
								select
								size="small"
								value={unidade}
								onChange={(evento) => setUnidade(evento.target.value)}
								inputProps={{ "aria-label": "Unidade do período" }}
								sx={styles.campoUnidade}
							>
								{UNIDADES.map((u) => (
									<MenuItem key={u.valor} value={u.valor}>
										{u.rotulo}
									</MenuItem>
								))}
							</TextField>
						</Box>
						<Typography variant="caption" color="text.secondary">
							Mesma trigger, neste equipamento. Máximo: 7 dias.
						</Typography>
						{periodoInvalido && (
							<Typography variant="caption" color="error">
								Informe um período entre 1 minuto e 7 dias.
							</Typography>
						)}
					</Box>
				)}

				{erro && (
					<Alert severity="error" variant="outlined">
						{erro}
					</Alert>
				)}
			</DialogContent>
			<DialogActions sx={styles.acoes}>
				<Button onClick={fechar} disabled={enviando}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={confirmar}
					disabled={enviando || periodoInvalido}
					startIcon={
						enviando ? <CircularProgress size={16} color="inherit" /> : null
					}
				>
					{enviando ? "Reconhecendo…" : "Reconhecer"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
